import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type { Wallet, WalletDeleteResolution, WalletWrite } from '../../lib/api-types';

export const walletKeys = { all: ['finance', 'wallets'] as const };

export async function fetchWallets(): Promise<Wallet[]> {
  return (await apiRequest<{ wallets: Wallet[] }>('/api/wallets')).wallets;
}

export async function createWallet(input: WalletWrite): Promise<Wallet> {
  return (
    await apiRequest<{ wallet: Wallet }>('/api/wallets', {
      body: jsonBody(input),
      method: 'POST',
    })
  ).wallet;
}

export async function updateWallet(id: string, input: WalletWrite): Promise<Wallet> {
  return (
    await apiRequest<{ wallet: Wallet }>(`/api/wallets/${id}`, {
      body: jsonBody(input),
      method: 'PUT',
    })
  ).wallet;
}

export function deleteWallet(id: string, resolution?: WalletDeleteResolution): Promise<void> {
  return apiRequest(`/api/wallets/${id}`, {
    ...(resolution ? { body: jsonBody(resolution) } : {}),
    method: 'DELETE',
  });
}

export function useWallets() {
  return useQuery({ queryFn: fetchWallets, queryKey: walletKeys.all });
}

export function useCreateWallet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createWallet,
    onSuccess: async () => client.invalidateQueries({ queryKey: walletKeys.all }),
  });
}

export function useUpdateWallet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: WalletWrite }) => updateWallet(id, input),
    onSuccess: async () => client.invalidateQueries({ queryKey: walletKeys.all }),
  });
}

export function useDeleteWallet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution?: WalletDeleteResolution }) =>
      deleteWallet(id, resolution),
    onSuccess: async () => client.invalidateQueries({ queryKey: walletKeys.all }),
  });
}
